define(["require", "exports", "preact/hooks", "../utils/environment", "../utils/jwt", "../utils/analytics", "../utils/traceability", "./authenticationErrors"], function (require, exports, hooks_1, environment_1, jwt_1, analytics_1, traceability_1, authenticationErrors_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    exports.useLogin = exports.logout = exports.login = void 0;
    const emailWhitelist = [
        "osp-selenium-user1_ww@oracle.com",
        "osp-selenium-user2_ww@oracle.com",
        "osp-selenium-user3_ww@oracle.com",
        "osp-notification-user_ww@oracle.com",
    ];
    const OAUTH_REQUEST_KEY = "oauth-request";
    const OAUTH_SESSION_KEY = "oauth-session";
    const AUTH_META_KEY = "auth-meta";
    const LEGACY_OAUTH_KEY = "oauth";
    const REDIRECTS_KEY = "redirects";
    const nowEpochInSeconds = () => Math.floor(Date.now() / 1000);
    const isRecord = (value) => typeof value === "object" && value !== null;
    const isNonEmptyString = (value) => typeof value === "string" && value.trim().length > 0;
    const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);
    const isOAuthRequestState = (value) => isRecord(value) &&
        isNonEmptyString(value.state) &&
        isNonEmptyString(value.nonce) &&
        isNonEmptyString(value.path);
    const isOAuthSession = (value) => isRecord(value) &&
        isNonEmptyString(value.at) &&
        isNonEmptyString(value.it) &&
        isFiniteNumber(value.exp);
    const isSharedAuthMeta = (value) => isRecord(value) &&
        isNonEmptyString(value.sub) &&
        isNonEmptyString(value.jti) &&
        isFiniteNumber(value.exp) &&
        isFiniteNumber(value.updatedAt);
    function readStoredJson(storage, key, guard) {
        try {
            const rawValue = storage.getItem(key);
            if (!rawValue) {
                return undefined;
            }
            const parsedValue = JSON.parse(rawValue);
            return guard(parsedValue) ? parsedValue : undefined;
        }
        catch (_a) {
            return undefined;
        }
    }
    function writeStoredJson(storage, key, value) {
        try {
            storage.setItem(key, JSON.stringify(value));
        }
        catch (_a) {
            (0, environment_1.debug)("[OCC] Failed to persist storage key", key);
        }
    }
    const removeStoredKeys = (storage, keys) => {
        keys.forEach((key) => {
            try {
                storage.removeItem(key);
            }
            catch (_a) {
                (0, environment_1.debug)("[OCC] Failed to remove storage key", key);
            }
        });
    };
    const readLegacyOauthValue = () => readStoredJson(sessionStorage, LEGACY_OAUTH_KEY, isRecord);
    const readOAuthRequestState = () => {
        const currentValue = readStoredJson(sessionStorage, OAUTH_REQUEST_KEY, isOAuthRequestState);
        if (currentValue) {
            return currentValue;
        }
        const legacyValue = readLegacyOauthValue();
        return isOAuthRequestState(legacyValue) ? legacyValue : undefined;
    };
    const readOAuthSession = () => {
        const currentValue = readStoredJson(sessionStorage, OAUTH_SESSION_KEY, isOAuthSession);
        if (currentValue) {
            return currentValue;
        }
        const legacyValue = readLegacyOauthValue();
        return isOAuthSession(legacyValue) ? legacyValue : undefined;
    };
    const readSharedAuthMeta = () => readStoredJson(localStorage, AUTH_META_KEY, isSharedAuthMeta);
    const persistOAuthRequestState = (value) => {
        writeStoredJson(sessionStorage, OAUTH_REQUEST_KEY, value);
        removeStoredKeys(sessionStorage, [LEGACY_OAUTH_KEY]);
    };
    const persistOAuthSession = (value) => {
        writeStoredJson(sessionStorage, OAUTH_SESSION_KEY, value);
        removeStoredKeys(sessionStorage, [LEGACY_OAUTH_KEY]);
    };
    const persistSharedAuthMeta = (value) => {
        writeStoredJson(localStorage, AUTH_META_KEY, value);
    };
    const clearOAuthRequestState = () => {
        removeStoredKeys(sessionStorage, [OAUTH_REQUEST_KEY, LEGACY_OAUTH_KEY]);
    };
    const clearOAuthSession = () => {
        removeStoredKeys(sessionStorage, [OAUTH_SESSION_KEY, LEGACY_OAUTH_KEY]);
    };
    const clearSharedAuthMeta = () => {
        removeStoredKeys(localStorage, [AUTH_META_KEY]);
    };
    const clearRedirects = () => {
        removeStoredKeys(sessionStorage, [REDIRECTS_KEY]);
    };
    const clearAllAuthStorage = () => {
        clearOAuthRequestState();
        clearOAuthSession();
        clearSharedAuthMeta();
        clearRedirects();
    };
    const clearAuthSessionStorage = (clearSharedMeta) => {
        clearOAuthSession();
        clearRedirects();
        if (clearSharedMeta) {
            clearSharedAuthMeta();
        }
    };
    const validateSession = (session) => {
        if (!session || session.exp <= nowEpochInSeconds()) {
            return undefined;
        }
        const accessTokenClaims = (0, jwt_1.parseJwtClaims)(session.at);
        const idTokenClaims = (0, jwt_1.parseJwtClaims)(session.it);
        if (!accessTokenClaims || !idTokenClaims) {
            return undefined;
        }
        if (accessTokenClaims.sub !== idTokenClaims.sub) {
            return undefined;
        }
        if (accessTokenClaims.exp <= nowEpochInSeconds() || idTokenClaims.exp <= nowEpochInSeconds()) {
            return undefined;
        }
        return {
            session,
            accessTokenClaims,
            idTokenClaims,
        };
    };
    const isStaleSession = (validatedSession, sharedAuthMeta) => {
        if (!sharedAuthMeta || sharedAuthMeta.exp <= nowEpochInSeconds()) {
            return false;
        }
        return sharedAuthMeta.sub === validatedSession.accessTokenClaims.sub &&
            sharedAuthMeta.jti !== validatedSession.accessTokenClaims.jti;
    };
    const getNonStaleSession = (session) => {
        const validatedSession = validateSession(session);
        if (!validatedSession) {
            return undefined;
        }
        return isStaleSession(validatedSession, readSharedAuthMeta()) ? undefined : validatedSession;
    };
    const redirectInternalUsers = (username, redirectUrl) => {
        const formattedUsername = username.trim().toLowerCase();
        if (!emailWhitelist.includes(formattedUsername) && formattedUsername.endsWith("@oracle.com") && redirectUrl) {
            clearAllAuthStorage();
            window.location.href = redirectUrl;
            return true;
        }
        return false;
    };
    const getCurrentPostLoginPath = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete("login");
        return url.href.replace(url.origin, "");
    };
    const normalizeRedirectPathOverride = (redirectPathOverride) => {
        if (!isNonEmptyString(redirectPathOverride)) {
            return undefined;
        }
        const trimmedRedirectPath = redirectPathOverride.trim();
        if (!trimmedRedirectPath.startsWith("/")) {
            return undefined;
        }
        try {
            const url = new URL(trimmedRedirectPath, window.location.origin);
            if (url.origin !== window.location.origin) {
                return undefined;
            }
            return url.href.replace(url.origin, "");
        }
        catch (_a) {
            return undefined;
        }
    };
    /**
     * Redirects the browser to the IDCS authorization flow and stores the OAuth request state.
     */
    const login = (environment) => (redirectPathOverride) => {
        var _a;
        (0, analytics_1.trackAnalytics)({ linkName: "Header - Sign In" });
        const state = crypto.randomUUID();
        const nonce = crypto.randomUUID();
        const url = new URL(window.location.href);
        url.searchParams.delete("login");
        persistOAuthRequestState({
            state,
            nonce,
            path: (_a = normalizeRedirectPathOverride(redirectPathOverride)) !== null && _a !== void 0 ? _a : getCurrentPostLoginPath(),
        });
        window.location.href = `${environment === null || environment === void 0 ? void 0 : environment.idcs}/oauth2/v1/authorize` +
            `?client_id=${environment === null || environment === void 0 ? void 0 : environment.idcsClientId}` +
            "&response_type=code" +
            `&redirect_uri=${encodeURIComponent(url.origin)}` +
            "&scope=openid%20urn:oracle:osp:osp-occ-framework" +
            `&nonce=${nonce}` +
            `&state=${state}`;
    };
    exports.login = login;
    /**
     * Clears local auth state, informs peer tabs, and redirects to the IDCS logout endpoint.
     */
    const logout = (environment, token, tokenChannel) => () => {
        clearAllAuthStorage();
        broadcastLogout(tokenChannel);
        window.location.href = `${environment === null || environment === void 0 ? void 0 : environment.idcs}/oauth2/v1/userlogout` +
            `?id_token_hint=${token}` +
            `&post_logout_redirect_uri=${encodeURIComponent(window.location.origin + "/")}`;
    };
    exports.logout = logout;
    const broadcast = (tokenChannel, msg) => {
        try {
            tokenChannel === null || tokenChannel === void 0 ? void 0 : tokenChannel.postMessage(msg);
            (0, environment_1.debug)(`[OCC] broadcastChannel - message ${tokenChannel ? "sent." : "sending failed."}`, JSON.stringify(msg));
        }
        catch (_a) {
            console.warn(`[OCC] Broadcast channel is already closed. Sending ${msg.type} message.`);
        }
    };
    const broadcastToken = (tokenChannel, session) => {
        broadcast(tokenChannel, { type: "TOKEN_UPDATED", session });
    };
    const broadcastTokenRequest = (tokenChannel) => {
        broadcast(tokenChannel, { type: "TOKEN_REQUESTED" });
    };
    const broadcastLogout = (tokenChannel) => {
        broadcast(tokenChannel, { type: "LOGOUT" });
    };
    const hasPendingOAuthCallback = () => {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.has("code") && searchParams.has("state");
    };
    const useLogin = (environment) => {
        const [userLogin, setUserLogin] = (0, hooks_1.useState)("");
        const [accessToken, setAccessToken] = (0, hooks_1.useState)("");
        const [accessTokenExpired, setAccessTokenExpired] = (0, hooks_1.useState)(false);
        const [idToken, setIdToken] = (0, hooks_1.useState)("");
        const [loading, setLoading] = (0, hooks_1.useState)(() => hasPendingOAuthCallback());
        const [triggerLogout, setTriggerLogout] = (0, hooks_1.useState)(false);
        const [error, setError] = (0, hooks_1.useState)();
        const [tokenChannel, setTokenChannel] = (0, hooks_1.useState)();
        const expiryTimerRef = (0, hooks_1.useRef)();
        const reauthRequiredRef = (0, hooks_1.useRef)(false);
        const lastIdTokenRef = (0, hooks_1.useRef)();
        const clearExpiryTimer = () => {
            if (expiryTimerRef.current !== undefined) {
                window.clearTimeout(expiryTimerRef.current);
                expiryTimerRef.current = undefined;
            }
        };
        const clearInMemoryAuthState = (preserveLogoutToken = false) => {
            clearExpiryTimer();
            if (!preserveLogoutToken) {
                lastIdTokenRef.current = undefined;
            }
            setAccessToken("");
            setIdToken("");
            setUserLogin("");
            setAccessTokenExpired(false);
        };
        const invalidateCurrentTabSession = (nextError) => {
            reauthRequiredRef.current = true;
            clearAuthSessionStorage(false);
            clearInMemoryAuthState(true);
            setError(nextError);
            setAccessTokenExpired(true);
        };
        const scheduleTokenExpiry = (expEpochInSec) => {
            clearExpiryTimer();
            setAccessTokenExpired(false);
            const expiryInMs = (expEpochInSec - nowEpochInSeconds()) * 1000;
            if (expiryInMs <= 0) {
                setAccessTokenExpired(true);
                return;
            }
            expiryTimerRef.current = window.setTimeout(() => setAccessTokenExpired(true), expiryInMs);
        };
        const applyAuthSession = (session, shouldBroadcast) => {
            var _a;
            if (!environment) {
                return false;
            }
            const validatedSession = validateSession(session);
            if (!validatedSession) {
                return false;
            }
            if (redirectInternalUsers(validatedSession.idTokenClaims.sub, environment.features.redirectInternal)) {
                clearInMemoryAuthState();
                return false;
            }
            persistOAuthSession(validatedSession.session);
            persistSharedAuthMeta({
                sub: validatedSession.accessTokenClaims.sub,
                jti: validatedSession.accessTokenClaims.jti,
                exp: validatedSession.session.exp,
                updatedAt: Date.now(),
            });
            reauthRequiredRef.current = false;
            lastIdTokenRef.current = validatedSession.session.it;
            setError(undefined);
            setAccessToken(validatedSession.session.at);
            setIdToken(validatedSession.session.it);
            setUserLogin((_a = validatedSession.idTokenClaims.user_displayname) !== null && _a !== void 0 ? _a : validatedSession.idTokenClaims.sub);
            scheduleTokenExpiry(validatedSession.session.exp);
            if (shouldBroadcast) {
                broadcastToken(tokenChannel, validatedSession.session);
            }
            return true;
        };
        const startLogin = (redirectPathOverride) => {
            reauthRequiredRef.current = false;
            (0, exports.login)(environment)(redirectPathOverride);
        };
        const startLogout = () => {
            reauthRequiredRef.current = false;
            const logoutToken = idToken || lastIdTokenRef.current;
            if (!logoutToken) {
                clearAllAuthStorage();
                clearInMemoryAuthState();
                setTriggerLogout(false);
                return;
            }
            (0, exports.logout)(environment, logoutToken, tokenChannel)();
        };
        const handleUnauthenticatedState = (searchParams) => {
            clearAuthSessionStorage(false);
            clearInMemoryAuthState();
            if (!(0, environment_1.isPublicZone)(environment)) {
                if (window.location.pathname !== "/" || (environment === null || environment === void 0 ? void 0 : environment.features.autoLogin) || searchParams.has("login")) {
                    startLogin();
                }
                else if (window.location.pathname === "/") {
                    if (!reauthRequiredRef.current) {
                        broadcastTokenRequest(tokenChannel);
                    }
                    setLoading(false);
                }
            }
            else {
                setLoading(false);
            }
        };
        const restoreOriginalPath = (path) => {
            window.history.replaceState({}, document.title, path !== null && path !== void 0 ? path : window.location.pathname);
        };
        (0, hooks_1.useEffect)(() => {
            // Delay channel creation until the environment is available so the callback
            // effect does not run once with a stale pre-environment channel and again
            // with the real channel during initial app bootstrap.
            if (!environment) {
                return;
            }
            const broadcastChannel = new BroadcastChannel("auth-tokens");
            broadcastChannel.onmessage = (event) => {
                var _a, _b, _c;
                (0, environment_1.debug)("[OCC] broadcastChannel - message received.", JSON.stringify(event === null || event === void 0 ? void 0 : event.data));
                if (((_a = event.data) === null || _a === void 0 ? void 0 : _a.type) === "TOKEN_UPDATED") {
                    if (reauthRequiredRef.current) {
                        (0, environment_1.debug)("[OCC] broadcastChannel - Ignoring token update until explicit re-authentication.");
                        return;
                    }
                    const incomingSession = validateSession(event.data.session);
                    if (!incomingSession) {
                        (0, environment_1.debug)("[OCC] broadcastChannel - Ignoring token update (claims mismatch/invalid).");
                        return;
                    }
                    const currentSession = validateSession(readOAuthSession());
                    if (currentSession && currentSession.accessTokenClaims.sub !== incomingSession.accessTokenClaims.sub) {
                        (0, environment_1.debug)("[OCC] broadcastChannel - Different subject detected; invalidating local tab session.");
                        invalidateCurrentTabSession(new authenticationErrors_1.AuthenticationChangedInAnotherTabError());
                        return;
                    }
                    if (isStaleSession(incomingSession, readSharedAuthMeta())) {
                        (0, environment_1.debug)("[OCC] broadcastChannel - Ignoring stale token update.");
                        return;
                    }
                    const sessionApplied = applyAuthSession(incomingSession.session, false);
                    if (!sessionApplied) {
                        (0, environment_1.debug)("[OCC] broadcastChannel - Ignoring token update (claims mismatch/invalid).");
                    }
                }
                else if (((_b = event.data) === null || _b === void 0 ? void 0 : _b.type) === "TOKEN_REQUESTED") {
                    if (reauthRequiredRef.current) {
                        (0, environment_1.debug)("[OCC] broadcastChannel - Ignoring token request until explicit re-authentication.");
                        return;
                    }
                    const currentSession = getNonStaleSession(readOAuthSession());
                    if (currentSession) {
                        broadcastToken(broadcastChannel, currentSession.session);
                    }
                }
                else if (((_c = event.data) === null || _c === void 0 ? void 0 : _c.type) === "LOGOUT") {
                    broadcastChannel.close();
                    setTriggerLogout(true);
                }
            };
            setTokenChannel(broadcastChannel);
            return () => {
                clearExpiryTimer();
                broadcastChannel.close();
            };
        }, [environment]);
        (0, hooks_1.useEffect)(() => {
            if (!environment || !tokenChannel) {
                setLoading(true);
                return;
            }
            const searchParams = new URLSearchParams(window.location.search);
            const requestState = readOAuthRequestState();
            if (searchParams.has("reset")) {
                clearAllAuthStorage();
                clearInMemoryAuthState();
                setLoading(false);
                window.history.pushState({}, document.title, window.location.pathname);
                return;
            }
            if (searchParams.has("code") && searchParams.has("state")) {
                const receivedState = searchParams.get("state");
                if (receivedState !== (requestState === null || requestState === void 0 ? void 0 : requestState.state)) {
                    clearOAuthRequestState();
                    restoreOriginalPath(requestState === null || requestState === void 0 ? void 0 : requestState.path);
                    setError(new Error("OAuth state mismatch"));
                    setLoading(false);
                    return;
                }
                setLoading(true);
                const code = searchParams.get("code");
                fetch(`${environment_1.apiUrl}/auth/login`, {
                    method: "POST",
                    headers: new Headers({
                        "Content-Type": "application/json",
                        [traceability_1.traceHeaderName]: (0, traceability_1.createTraceHeaderValue)(undefined),
                    }),
                    body: JSON.stringify({ code, nonce: requestState.nonce }),
                })
                    .then((response) => {
                    if (!response.ok) {
                        throw new Error("Login failed");
                    }
                    return response.json();
                })
                    .then((body) => {
                    const session = {
                        at: body.accessToken,
                        it: body.idToken,
                        exp: body.expiresAt,
                    };
                    if (!applyAuthSession(session, true)) {
                        throw new Error("Invalid tokens received");
                    }
                    clearOAuthRequestState();
                })
                    .catch((err) => {
                    setError(err);
                    clearAllAuthStorage();
                    clearInMemoryAuthState();
                })
                    .finally(() => {
                    setLoading(false);
                    restoreOriginalPath(requestState.path);
                });
                return;
            }
            const validatedSession = validateSession(readOAuthSession());
            if (validatedSession) {
                if (isStaleSession(validatedSession, readSharedAuthMeta())) {
                    clearAuthSessionStorage(false);
                    clearInMemoryAuthState();
                    handleUnauthenticatedState(searchParams);
                    return;
                }
                if (!applyAuthSession(validatedSession.session, false)) {
                    clearAllAuthStorage();
                    clearInMemoryAuthState();
                    setLoading(false);
                }
                else {
                    const url = new URL(window.location.href);
                    if (url.searchParams.has("login")) {
                        url.searchParams.delete("login");
                        window.history.pushState({}, document.title, url.pathname + url.search);
                    }
                    setLoading(false);
                }
                return;
            }
            handleUnauthenticatedState(searchParams);
        }, [environment, tokenChannel]);
        (0, hooks_1.useEffect)(() => {
            if (environment && tokenChannel && triggerLogout) {
                startLogout();
            }
        }, [environment, tokenChannel, triggerLogout]);
        return {
            loading,
            error,
            userLogin,
            idToken,
            accessToken,
            accessTokenExpired,
            login: startLogin,
            logout: startLogout,
        };
    };
    exports.useLogin = useLogin;
});
